import { useEffect, useMemo, useRef } from 'react';
import { FlatList, ScrollView, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { parsePhoneNumber } from 'awesome-phonenumber';
import dayjs from 'dayjs';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsEqual from 'lodash/isEqual';

import ReservationCancellationPolicy from '../../Components/ReservationCancellationPolicy';
import Form from '../../Components/FormFields/Form';
import FormFieldCheckbox from '../../Components/FormFields/FormFieldCheckbox';
import FormPaymentMethods from '../../Components/FormFields/FormPaymentMethods';
import SubmitButton from '../../Components/FormFields/SubmitButton';
import ProfileForm from '../../Components/ProfileForm';
import {
  formikNames,
  textCategory,
} from '../../Components/Reservation/constants';
import { ids, styles } from '../../Components/Reservation/styles';
import ResponsiveText from '../../Components/ResponsiveText';
import ResponsiveLayout from '../../Components/ScreenWrapper/ResponsiveLayout';
import modals from '../../Components/Sheets/modals';
import { ModalManager } from '../../Components/Web/Modal/hooks/useModalManager';
import constants from '../../Config/constants';
import validationRules from '../../Config/validationRules';
import { getReservationStoreParams } from '../../Helper';
import useGoBack from '../../Hooks/useGoBack';
import routeList from '../../Routes/list';
import {
  useAddToCartEventMutation,
  useCheckoutEventMutation,
} from '../../RTK/api/events';
import { useCheckoutReservationMutation } from '../../RTK/api/reservations';
import { userSelector } from '../../RTK/user/selectors';
import {
  removeSuccessfulOrder,
  setReservationCheckoutData,
} from '../../RTK/utility';
import { selectReservationCheckoutData } from '../../RTK/utility/selectors';
import themeStyle from '../../Theme/styles';

import OrderSummary from './components/OrderSummary';
import ReservationLeftColumnContent from './components/ReservationLeftColumnContent';
import SummaryAndPaymentWeb from './components/SummaryAndPaymentWeb';
import { ReservationCheckout as ReservationCheckoutType } from './types';
import Service from '../../Service';
import ErrorTransformer from '../../Service/ErrorTransformer';

const baseSchema = {
  firstName: validationRules.firstNameSchema,
  lastName: validationRules.lastNameSchema,
  phone: validationRules.internationalPhoneSchema,
  email: validationRules.emailSchema,
  date: validationRules.fieldRequired('Date'),
  time: validationRules.fieldRequired('Time'),
  location: validationRules.fieldRequired('Location'),
  size: validationRules.fieldRequired('Party Size', 'number'),
  payment: validationRules.paymentSchema,
  isUnderstood: validationRules.checkedSchema(
    'Must accept cancellation policy',
  ),
};
const ReservationValidationSchema = validationRules.createSchema({
  ...baseSchema,
  [formikNames.DIETARYORALLERGY]: validationRules.fieldRequired(
    'Dietary Preference',
    'array',
  ),
});
const EventValidationSchema = validationRules.createSchema(baseSchema);

const ReservationCheckout = ({ navigation }) => {
  const dispatch = useDispatch();
  const fieldsRef = useRef<{ setSubmitButtonRef?: () => void }>();
  const yLocRef = useRef<{ [key: string]: number }>({});
  const scrollRef = useRef<FlatList & ScrollView>();
  const [addToCartEvent] = useAddToCartEventMutation();
  const [checkoutEvent] = useCheckoutEventMutation();
  const [checkoutReservation] = useCheckoutReservationMutation();
  const user = useSelector(userSelector);
  const checkoutData: ReservationCheckoutType = useSelector(
    selectReservationCheckoutData,
  );

  const goHome = useGoBack();
  const goBackToEvent = useGoBack({
    isDefaultGoBack: false,
    backRoute: routeList.RES_EVENT_DETAILS,
    backRouteParams: {
      eventId: checkoutData?.itemId,
      size: checkoutData?.size,
    },
  });

  const formInitialValues = useMemo(
    () => ({
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      phone: user?.phone?.replace('+63', '') || '',
      email: user?.email || '',
      date: checkoutData?.selectedDate,
      time: checkoutData?.selectedTime,
      location: checkoutData?.address,
      size: checkoutData?.size,
      payment: checkoutData?.formData?.payment,
      isUnderstood: undefined,
      [formikNames.DIETARYORALLERGY]:
        checkoutData?.formData?.[formikNames.DIETARYORALLERGY] || [],
      [formikNames.CELEBRATIONS]:
        checkoutData?.formData?.[formikNames.CELEBRATIONS],
    }),
    [user, checkoutData],
  );

  useEffect(() => {
    if (!checkoutData?.type) {
      goHome();
    }
  }, [checkoutData?.type, goHome]);

  const _onError = (data: { [key: string]: string }) => {
    const firstKey = Object.keys(data)[0];
    const scrollLoc = yLocRef.current[firstKey];
    if (scrollLoc) {
      if (constants.isWeb) {
        scrollRef.current.scrollToOffset({ animated: true, offset: scrollLoc });
      } else {
        scrollRef.current.scrollTo({ animated: true, y: scrollLoc });
      }
    }
  };

  const _onSubmit = async (formData, { setErrors }) => {
    const phoneInput = formData.phone?.trim?.() ?? '';
    const mobileNumber =
      parsePhoneNumber(phoneInput, { regionCode: 'PH' }).number?.e164 ??
      phoneInput;
    const noUndefinedFormData = Object.keys(formData).reduce((acc, key) => {
      if (formData[key] !== undefined) {
        acc[key] = formData[key];
      }
      return acc;
    }, {});
    const isFormDataChanged = !lodashIsEqual(
      checkoutData.formData,
      noUndefinedFormData,
    );
    if (isFormDataChanged) {
      return await new Promise((resolve) => {
        // remove previously successful order flag for this store
        dispatch(removeSuccessfulOrder(checkoutData.storeId));
        const checkoutPromise = checkoutData?.isReservation
          ? checkoutReservation({
              payload: {
                tables: [
                  {
                    store_id: checkoutData.storeId,
                    id: checkoutData.itemId,
                    quantity: checkoutData.size,
                    extras: [],
                  },
                  ...checkoutData?.addOns?.map((addOn) => ({
                    store_id: checkoutData.storeId,
                    id: addOn.id,
                    quantity: checkoutData.size,
                    extras: [],
                  })),
                ],
                customer: {
                  email: formData.email?.trim?.(),
                  first_name: formData.firstName?.trim?.(),
                  id: user?.id,
                  last_name: formData.lastName?.trim?.(),
                  mobile_number: mobileNumber,
                },
                reservation_date: checkoutData.selectedDate,
                time: checkoutData.selectedTime,
                payment_method: formData.payment.method,
                remarks: JSON.stringify({
                  dietary: formData.dietaryOrAllergy,
                  celebration: formData.celebrations,
                }),
              },
            })
          : addToCartEvent({
              extras: [],
              instructions: {},
              item_id: checkoutData?.addOns?.[0]?.id,
              order_type: 'pickup',
              quantity: checkoutData.size,
              store_id: checkoutData.storeId,
            }).then((addRes) => {
              if ('error' in addRes) {
                setErrors({ api: { message: addRes.error } });
                return Promise.resolve(null);
              } else {
                return checkoutEvent({
                  payload: {
                    order_date: checkoutData.selectedDate,
                    order_time: checkoutData.selectedTime,
                    payment_method: formData.payment.method,
                    store_id: checkoutData.storeId,
                    customer: {
                      email: formData.email,
                      first_name: formData.firstName,
                      last_name: formData.lastName,
                      mobile_number: mobileNumber,
                    },
                  },
                });
              }
            });
        checkoutPromise.then((checkRes) => {
          if ('error' in checkRes) {
            const errMsg = checkRes.error as string;
            if (
              Object.keys(constants.EVENT_SOLDOUT)
                .map(
                  (e) => constants.EVENT_SOLDOUT[e], // get key value
                )
                .includes(errMsg)
            ) {
              // if sold out
              _soldOutPrompt(errMsg);
            } else {
              setErrors({ api: { message: errMsg } });
            }
            resolve(null);
          } else if (!lodashIsEmpty(checkRes) && checkRes?.data?.redirect) {
            // response must not be empty and must have redirect url to proceed to checkout webview
            const checkoutParam = {
              // need on checkout webview
              storeId: checkoutData.storeId,
              url: checkRes.data.redirect,
              payment: formData.payment,
              backRoute: routeList.RES_STORE_DETAILS,
              backRouteParams: getReservationStoreParams(
                { checkoutData },
                checkoutData?.isReservation ? 'dining' : 'event',
              ),
              successRoute: {
                tab: routeList.RESERVATION_TAB,
                screen: routeList.MY_RESERVATION_DETAILS,
                params: {
                  success: true,
                  reservation: checkoutData.isReservation,
                },
              },
              waitSuccess: true, // the checkout will wait the success payment instead of redirecting to success while the payment still verifying
              // need on continue to checkout
              checkout_date: dayjs().toISOString(),
              ...checkRes.data,
            };
            dispatch(
              setReservationCheckoutData({
                ...checkoutData,
                ...checkoutParam,
                formData: noUndefinedFormData,
              }),
            );
            resolve(null);
            navigation.navigate(routeList.CHECKOUT_WEBVIEW, {
              ...checkoutParam,
              backRoute: routeList.RESERVATION_TAB,
              backParams: {
                screen: routeList.RES_CHECKOUT,
              },
            });
          } else {
            setErrors({
              api: {
                message: ErrorTransformer['Something went wrong on our end.'],
              },
            });
          }
        });
      });
    } else {
      navigation.navigate(routeList.CHECKOUT_WEBVIEW, {
        ...checkoutData,
        backRoute: routeList.RESERVATION_TAB,
        backParams: {
          screen: routeList.RES_CHECKOUT,
        },
      });
      return Promise.resolve(null);
    }
  };

  const _soldOutPrompt = async (soldOutMsg: string) => {
    const isOverallSoldOut = soldOutMsg === constants.EVENT_SOLDOUT.ALL;
    const promptPayload = {
      title: isOverallSoldOut ? 'Sold Out' : 'Please try another date.',
      message: isOverallSoldOut
        ? "We're sorry, but all the slots for this event just sold out."
        : "We're sorry, but all slots for this date have been filled.",
      buttonText: 'Ok',
      buttonStatus: 'info',
    };
    if (constants.isWeb) {
      ModalManager.show(modals.PROMPT, {
        payload: promptPayload,
      });
    } else {
      modals.show(modals.PROMPT, promptPayload);
    }
    goBackToEvent();
  };

  if (lodashIsEmpty(checkoutData)) return null; // return nothing, it will be redirected anyway

  return (
    <Form
      initialValues={formInitialValues}
      validationSchema={
        checkoutData?.isReservation
          ? ReservationValidationSchema
          : EventValidationSchema
      }
      onSubmit={_onSubmit}
      enableReinitialize
    >
      <ResponsiveLayout
        title="Complete your experience"
        LeftComponent={
          <View
            style={[
              themeStyle.flex1,
              !constants.isWeb && themeStyle.pageHorizontalSpacing,
            ]}
          >
            <View style={themeStyle.spacingTop}>
              <OrderSummary />
            </View>
            <View
              dataSet={{ media: ids.detailsBox }}
              style={styles.detailsBox}
              onLayout={(event) => {
                const layout = event.nativeEvent.layout;
                yLocRef.current.firstName = layout.y;
                yLocRef.current.lastName = layout.y;
                yLocRef.current.phone = layout.y;
                yLocRef.current.email = layout.y;
              }}
            >
              <ResponsiveText
                category={textCategory.TITLE}
                style={themeStyle.spacingBottomMedium}
              >
                Customer Information
              </ResponsiveText>
              <ProfileForm
                ref={fieldsRef}
                disableEmail={false}
                internationalPhone
                useFormikContext
              />
            </View>
            {checkoutData.isReservation && (
              <ReservationLeftColumnContent yLocRef={yLocRef} />
            )}
            <ReservationCancellationPolicy
              value={checkoutData.cancellationPolicy}
            />
            <FormFieldCheckbox
              label="I have read and understood the cancellation policy"
              name="isUnderstood"
              status="info"
              onLayout={(event) => {
                const layout = event.nativeEvent.layout;
                yLocRef.current.isUnderstood = layout.y;
              }}
            />
          </View>
        }
        RightComponent={<SummaryAndPaymentWeb onFormikError={_onError} />}
        MobileBottomComponent={
          <View
            style={[
              themeStyle.spacingTopMedium,
              !constants.isWeb && themeStyle.pageHorizontalSpacing,
              !constants.isWeb && themeStyle.spacingBottom,
            ]}
          >
            <FormPaymentMethods name="payment" status="info" />
            <SubmitButton
              status="info"
              text="Place Order"
              onError={_onError}
              onRef={fieldsRef.current?.setSubmitButtonRef}
            />
          </View>
        }
        onRef={(r) => (scrollRef.current = r)}
        withTitleAnimation={false}
        withFooter={false}
      />
    </Form>
  );
};

export default ReservationCheckout;
